import flatMap from 'lodash/flatMap';
import sortBy from 'lodash/sortBy';

import {NUMERIC_MATH_TYPES, PRIMATIVE_MATH_TYPES, XAU_MATH_TYPES} from 'common/report/constants';
import {Bucket} from 'common/widgets/bucket-pane/types';
import {ResourceType} from 'common/widgets/property-filter-menu/models/types';
import {capitalize} from 'common/util/string';

import {FIRST_TIME_PROPERTY} from '../query-block/properties-menu/constants';
import {
  AggregationLabelParams,
  SelectorType,
  SelectorTypeAggregationItem,
  SelectorTypeAggregationSection,
  SelectorTypeDefinitions,
} from './types';

export enum ControlBarAction {
  Filter = `filter`,
  FirstTimeFilter = `firstTimeFilter`,
  Rename = `rename`,
  Duplicate = `duplicate`,
  Delete = `delete`,
  CustomizeBuckets = `customizeBuckets`,
  AddGroupedEvent = `addGroupedEvent`,
  MapLookupTable = `mapLookupTable`,
  UnmapLookupTable = `unmapLookupTable`,
}

export const ControlBarActionItems = {
  [ControlBarAction.Filter]: {
    value: ControlBarAction.Filter,
    label: `Add Filter`,
    shortLabel: `Filter`,
    icon: `filter`,
  },
  [ControlBarAction.FirstTimeFilter]: {
    value: ControlBarAction.FirstTimeFilter,
    label: `Add 1st time filter`,
    icon: `type-boolean`,
    tooltipData: {
      ...FIRST_TIME_PROPERTY.tooltipData,
      header: `Add 1st time filter`,
    },
  },
  [ControlBarAction.Rename]: {
    value: ControlBarAction.Rename,
    icon: `edit`,
    label: `Rename`,
  },
  [ControlBarAction.Duplicate]: {
    value: ControlBarAction.Duplicate,
    icon: `duplicate`,
    label: `Duplicate`,
  },
  [ControlBarAction.Delete]: {
    value: ControlBarAction.Delete,
    icon: `trashcan`,
    label: `Delete`,
  },
  [ControlBarAction.CustomizeBuckets]: {
    value: ControlBarAction.CustomizeBuckets,
    icon: `custom`,
    label: `Customize Range`,
  },
  [ControlBarAction.AddGroupedEvent]: {
    value: ControlBarAction.AddGroupedEvent,
    icon: `event`,
    label: `Add event comparison`,
    tooltipData: {
      header: `Add event comparison`,
      description: `Choose multiple events at a particular funnel step to understand how conversion rates compare based on the action users take at that step.`,
      helpLink: {
        label: `More on comparison events`,
        href: `https://help.mixpanel.com/hc/en-us/articles/360025344831-Interpret-a-Funnels-Report#comparison-events`,
      },
    },
  },
  [ControlBarAction.MapLookupTable]: {
    value: ControlBarAction.MapLookupTable,
    icon: `compose`,
    label: `Map to Lookup Table`,
  },
  [ControlBarAction.UnmapLookupTable]: {
    value: ControlBarAction.UnmapLookupTable,
    icon: `x`,
    label: `Unmap Lookup Table`,
  },
};

const HELP_DOCS_LINK = `\n\n[More on data functions](https://help.mixpanel.com/hc/en-us/articles/115004565046)`;

export const SelectorTypeAggregations: {
  [key in SelectorType]?: SelectorTypeAggregationItem[];
} = {
  [ResourceType.Event]: [
    {
      label: `Total`,
      value: {
        order: -2,
        selectorType: ResourceType.Event,
        operator: PRIMATIVE_MATH_TYPES.TOTAL,
        description: `The total number of events performed${HELP_DOCS_LINK}`,
      },
    },
    {
      label: ({group}: AggregationLabelParams) => `Total per ${group}`,
      subLabel: `Distribution, Average, Percentiles`,
      value: {
        selectorType: ResourceType.Event,
        perUserOperator: PRIMATIVE_MATH_TYPES.PER_USER_TOTAL,
        description: ({group}: AggregationLabelParams) =>
          `The number of events performed per ${group}\n\nAdditional functions available: Distribution, Average, Median, Minimum, Maximum${HELP_DOCS_LINK}`,
        subAggregations: [
          {
            label: `Average`,
            value: {
              selectorType: ResourceType.Event,
              operator: PRIMATIVE_MATH_TYPES.AVERAGE,
              perUserOperator: PRIMATIVE_MATH_TYPES.PER_USER_TOTAL,
              description: ({group}: AggregationLabelParams) =>
                `Average across all users of the event count per ${group}${HELP_DOCS_LINK}`,
            },
          },
          {
            label: `Distribution`,
            value: {
              selectorType: ResourceType.Event,
              operator: PRIMATIVE_MATH_TYPES.HISTOGRAM,
              perUserOperator: PRIMATIVE_MATH_TYPES.PER_USER_TOTAL,
              description: ({group}: AggregationLabelParams) =>
                `A distribution of the number of times an event is performed per ${group}${HELP_DOCS_LINK}`,
            },
          },
          {
            label: `Median`,
            value: {
              selectorType: ResourceType.Event,
              operator: PRIMATIVE_MATH_TYPES.MEDIAN,
              perUserOperator: PRIMATIVE_MATH_TYPES.PER_USER_TOTAL,
              description: ({group}: AggregationLabelParams) =>
                `Median of the event count per ${group}${HELP_DOCS_LINK}`,
            },
          },
          {
            label: `25th Percentile`,
            value: {
              selectorType: ResourceType.Event,
              operator: NUMERIC_MATH_TYPES.P25,
              perUserOperator: PRIMATIVE_MATH_TYPES.PER_USER_TOTAL,
              shortName: `P25`,
              description: ({group}: AggregationLabelParams) =>
                `The 25th percentile of the event count per ${group}${HELP_DOCS_LINK}`,
            },
          },
          {
            label: `75th Percentile`,
            value: {
              selectorType: ResourceType.Event,
              operator: NUMERIC_MATH_TYPES.P75,
              perUserOperator: PRIMATIVE_MATH_TYPES.PER_USER_TOTAL,
              shortName: `P75`,
              description: ({group}: AggregationLabelParams) =>
                `The 75th percentile of the event count per ${group}${HELP_DOCS_LINK}`,
            },
          },
          {
            label: `90th Percentile`,
            value: {
              selectorType: ResourceType.Event,
              operator: NUMERIC_MATH_TYPES.P90,
              perUserOperator: PRIMATIVE_MATH_TYPES.PER_USER_TOTAL,
              shortName: `P90`,
              description: ({group}: AggregationLabelParams) =>
                `The 90th percentile of the event count per ${group}${HELP_DOCS_LINK}`,
            },
          },
          {
            label: `Minimum`,
            value: {
              selectorType: ResourceType.Event,
              operator: PRIMATIVE_MATH_TYPES.MIN,
              perUserOperator: PRIMATIVE_MATH_TYPES.PER_USER_TOTAL,
              description: ({group}: AggregationLabelParams) =>
                `Minimum of the number of time a ${group} performed an event${HELP_DOCS_LINK}`,
            },
          },
          {
            label: `Maximum`,
            value: {
              selectorType: ResourceType.Event,
              operator: PRIMATIVE_MATH_TYPES.MAX,
              perUserOperator: PRIMATIVE_MATH_TYPES.PER_USER_TOTAL,
              description: ({group}: AggregationLabelParams) =>
                `Maximum of the number of time a ${group} performed an event${HELP_DOCS_LINK}`,
            },
          },
        ],
      },
    },
    {
      label: `Sessions with event`,
      value: {
        selectorType: ResourceType.Event,
        operator: PRIMATIVE_MATH_TYPES.SESSIONS,
        description: `The number of sessions that contain the selected event${HELP_DOCS_LINK}`,
        shortLabel: `Sessions`,
        shortName: `Sessions`,
      },
    },
  ],

  [ResourceType.User]: [
    {
      label: `Unique`,
      value: {
        order: -1,
        selectorType: ResourceType.Event,
        operator: PRIMATIVE_MATH_TYPES.UNIQUE,
        description: ({pluralGroup}: AggregationLabelParams) =>
          `The number of ${pluralGroup} who performed an event${HELP_DOCS_LINK}`,
        shortName: `Unique`,
      },
    },
    {
      label: `Total`,
      value: {
        selectorType: ResourceType.User,
        operator: PRIMATIVE_MATH_TYPES.TOTAL,
        description: `The number of profiles`,
      },
    },
    {
      label: ({pluralGroupTitle}: AggregationLabelParams) => `Daily Active ${pluralGroupTitle} (DAU)`,
      value: {
        selectorType: ResourceType.Event,
        operator: XAU_MATH_TYPES.DAU,
        shortLabel: `DAU`,
        shortName: `DAU`,
        description: ({pluralGroup}: AggregationLabelParams) =>
          `The number of ${pluralGroup} who performed an event within the last 24 hours${HELP_DOCS_LINK}`,
      },
    },
    {
      label: ({pluralGroupTitle}: AggregationLabelParams) => `Weekly Active ${pluralGroupTitle} (WAU)`,
      value: {
        selectorType: ResourceType.Event,
        operator: XAU_MATH_TYPES.WAU,
        shortLabel: `WAU`,
        shortName: `WAU`,
        description: ({pluralGroup}: AggregationLabelParams) =>
          `The number of ${pluralGroup} who performed an event within the last 7 days${HELP_DOCS_LINK}`,
      },
    },
    {
      label: ({pluralGroupTitle}: AggregationLabelParams) => `Monthly Active ${pluralGroupTitle} (MAU)`,
      value: {
        selectorType: ResourceType.Event,
        operator: XAU_MATH_TYPES.MAU,
        shortLabel: `MAU`,
        shortName: `MAU`,
        description: ({pluralGroup}: AggregationLabelParams) =>
          `The number of ${pluralGroup} who performed an event within the last 30 days${HELP_DOCS_LINK}`,
      },
    },
  ],

  // count property aggregations
  [SelectorTypeDefinitions.EventPropertyAggregation]: [
    {
      label: `Sum`,
      value: {
        selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
        operator: PRIMATIVE_MATH_TYPES.TOTAL,
        shortLabel: `Sum of property value`,
        description: `The total of a numeric property value across all instances of an event${HELP_DOCS_LINK}`,
      },
    },
    {
      label: `Sum`,
      value: {
        selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
        shortLabel: ({group}: AggregationLabelParams) => `Sum of property value per ${group}`,
        perUserOperator: PRIMATIVE_MATH_TYPES.PER_USER_TOTAL,
        description: ({group}: AggregationLabelParams) =>
          `The total of numeric property values per ${group}\n\nAdditional functions available: Distribution, Average, Median, Minimum, Maximum${HELP_DOCS_LINK}`,
        subAggregations: [
          {
            label: `Average`,
            value: {
              selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
              operator: PRIMATIVE_MATH_TYPES.AVERAGE,
              perUserOperator: PRIMATIVE_MATH_TYPES.PER_USER_TOTAL,
              description: ({group}: AggregationLabelParams) =>
                `Average of the sum of property values per ${group}${HELP_DOCS_LINK}`,
            },
          },
          {
            label: `Distribution`,
            value: {
              selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
              operator: PRIMATIVE_MATH_TYPES.HISTOGRAM,
              perUserOperator: PRIMATIVE_MATH_TYPES.PER_USER_TOTAL,
              description: ({pluralGroup}: AggregationLabelParams) =>
                `A distribution of ${pluralGroup} by the sum of property values${HELP_DOCS_LINK}`,
            },
          },
          {
            label: `Median`,
            value: {
              selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
              operator: PRIMATIVE_MATH_TYPES.MEDIAN,
              perUserOperator: PRIMATIVE_MATH_TYPES.PER_USER_TOTAL,
              description: ({group}: AggregationLabelParams) =>
                `Median of the sum of property values per ${group}${HELP_DOCS_LINK}`,
            },
          },
          {
            label: `25th Percentile`,
            value: {
              selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
              operator: NUMERIC_MATH_TYPES.P25,
              perUserOperator: PRIMATIVE_MATH_TYPES.PER_USER_TOTAL,
              shortName: `P25`,
              description: ({group}: AggregationLabelParams) =>
                `The 25th percentile of the sum of property values per ${group}${HELP_DOCS_LINK}`,
            },
          },
          {
            label: `75th Percentile`,
            value: {
              selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
              operator: NUMERIC_MATH_TYPES.P75,
              perUserOperator: PRIMATIVE_MATH_TYPES.PER_USER_TOTAL,
              shortName: `P75`,
              description: ({group}: AggregationLabelParams) =>
                `The 75th percentile of the sum of property values per ${group}${HELP_DOCS_LINK}`,
            },
          },
          {
            label: `90th Percentile`,
            value: {
              selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
              operator: NUMERIC_MATH_TYPES.P90,
              perUserOperator: PRIMATIVE_MATH_TYPES.PER_USER_TOTAL,
              shortName: `P90`,
              description: ({group}: AggregationLabelParams) =>
                `The 90th percentile of the sum of property values per ${group}${HELP_DOCS_LINK}`,
            },
          },
          {
            label: `Minimum`,
            value: {
              selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
              operator: PRIMATIVE_MATH_TYPES.MIN,
              perUserOperator: PRIMATIVE_MATH_TYPES.PER_USER_TOTAL,
              description: ({group}: AggregationLabelParams) =>
                `Minimum of the sum of property values per ${group}${HELP_DOCS_LINK}`,
            },
          },
          {
            label: `Maximum`,
            value: {
              selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
              operator: PRIMATIVE_MATH_TYPES.MAX,
              perUserOperator: PRIMATIVE_MATH_TYPES.PER_USER_TOTAL,
              description: ({group}: AggregationLabelParams) =>
                `Maximum of the sum of property values per ${group}${HELP_DOCS_LINK}`,
            },
          },
        ],
      },
    },
    {
      label: `Average`,
      value: {
        selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
        operator: PRIMATIVE_MATH_TYPES.AVERAGE,
        shortLabel: `Average of property value`,
        description: `Average of a numeric property value across all instances of an event${HELP_DOCS_LINK}`,
      },
    },
    {
      label: `Average`,
      value: {
        selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
        shortLabel: ({group}: AggregationLabelParams) => `Average of property value per ${group}`,
        perUserOperator: PRIMATIVE_MATH_TYPES.PER_USER_AVERAGE,
        description: ({group}: AggregationLabelParams) =>
          `Average of numeric property values per ${group}\n\nAdditional functions available: Distribution, Average, Median, Minimum, Maximum${HELP_DOCS_LINK}`,
        subAggregations: [
          {
            label: `Average`,
            value: {
              selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
              operator: PRIMATIVE_MATH_TYPES.AVERAGE,
              perUserOperator: PRIMATIVE_MATH_TYPES.PER_USER_AVERAGE,
              description: ({group}: AggregationLabelParams) =>
                `Average of the average of property values per ${group}${HELP_DOCS_LINK}`,
            },
          },
          {
            label: `Distribution`,
            value: {
              selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
              operator: PRIMATIVE_MATH_TYPES.HISTOGRAM,
              perUserOperator: PRIMATIVE_MATH_TYPES.PER_USER_AVERAGE,
              description: ({pluralGroup}: AggregationLabelParams) =>
                `A distribution of ${pluralGroup} by the average of property values${HELP_DOCS_LINK}`,
            },
          },
          {
            label: `Median`,
            value: {
              selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
              operator: PRIMATIVE_MATH_TYPES.MEDIAN,
              perUserOperator: PRIMATIVE_MATH_TYPES.PER_USER_AVERAGE,
              description: ({group}: AggregationLabelParams) =>
                `Median of the average of property values per ${group}${HELP_DOCS_LINK}`,
            },
          },
          {
            label: `25th Percentile`,
            value: {
              selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
              operator: NUMERIC_MATH_TYPES.P25,
              perUserOperator: PRIMATIVE_MATH_TYPES.PER_USER_AVERAGE,
              shortName: `P25`,
              description: ({group}: AggregationLabelParams) =>
                `The 25th percentile of the average of property values per ${group}${HELP_DOCS_LINK}`,
            },
          },
          {
            label: `75th Percentile`,
            value: {
              selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
              operator: NUMERIC_MATH_TYPES.P75,
              perUserOperator: PRIMATIVE_MATH_TYPES.PER_USER_AVERAGE,
              shortName: `P75`,
              description: ({group}: AggregationLabelParams) =>
                `The 75th percentile of the average of property values per ${group}${HELP_DOCS_LINK}`,
            },
          },
          {
            label: `90th Percentile`,
            value: {
              selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
              operator: NUMERIC_MATH_TYPES.P90,
              perUserOperator: PRIMATIVE_MATH_TYPES.PER_USER_AVERAGE,
              shortName: `P90`,
              description: ({group}: AggregationLabelParams) =>
                `The 90th percentile of the average of property values per ${group}${HELP_DOCS_LINK}`,
            },
          },
          {
            label: `Minimum`,
            value: {
              selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
              operator: PRIMATIVE_MATH_TYPES.MIN,
              perUserOperator: PRIMATIVE_MATH_TYPES.PER_USER_AVERAGE,
              description: ({group}: AggregationLabelParams) =>
                `Minimum of the average of property values per ${group}${HELP_DOCS_LINK}`,
            },
          },
          {
            label: `Maximum`,
            value: {
              selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
              operator: PRIMATIVE_MATH_TYPES.MAX,
              perUserOperator: PRIMATIVE_MATH_TYPES.PER_USER_AVERAGE,
              description: ({group}: AggregationLabelParams) =>
                `Maximum of the average of property values per ${group}${HELP_DOCS_LINK}`,
            },
          },
        ],
      },
    },
    {
      label: `Median`,
      value: {
        selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
        operator: PRIMATIVE_MATH_TYPES.MEDIAN,
        shortLabel: `Median of property value`,
        description: `Median of a numeric property value across all instances of an event${HELP_DOCS_LINK}`,
      },
    },
    {
      label: `Distinct Count`,
      value: {
        selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
        operator: PRIMATIVE_MATH_TYPES.UNIQUE_VALUES,
        shortLabel: `Distinct property value count`,
        description: `The number of distinct property values across all instances of an event${HELP_DOCS_LINK}`,
      },
    },
    {
      label: `Most Frequent`,
      value: {
        selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
        operator: PRIMATIVE_MATH_TYPES.MOST_FREQUENT,
        shortLabel: `Most frequent property value`,
        description: `The most frequent property value across all instances of an event${HELP_DOCS_LINK}`,
      },
    },
    {
      label: `25th Percentile`,
      value: {
        selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
        operator: NUMERIC_MATH_TYPES.P25,
        shortLabel: `P25 of property value`,
        shortName: `P25`,
        description: `The 25th percentile of a numeric property value across all instances of an event${HELP_DOCS_LINK}`,
      },
    },
    {
      label: `75th Percentile`,
      value: {
        selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
        operator: NUMERIC_MATH_TYPES.P75,
        shortLabel: `P75 of property value`,
        shortName: `P75`,
        description: `The 75th percentile of a numeric property value across all instances of an event${HELP_DOCS_LINK}`,
      },
    },
    {
      label: `90th Percentile`,
      value: {
        selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
        operator: NUMERIC_MATH_TYPES.P90,
        shortLabel: `P90 of property value`,
        shortName: `P90`,
        description: `The 90th percentile of a numeric property value across all instances of an event${HELP_DOCS_LINK}`,
      },
    },
    {
      label: `99th Percentile`,
      value: {
        selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
        operator: NUMERIC_MATH_TYPES.P99,
        shortLabel: `P99 of property value`,
        shortName: `P99`,
        description: `The 99th percentile of a numeric property value across all instances of an event${HELP_DOCS_LINK}`,
      },
    },
    {
      label: `Distinct Count`,
      value: {
        selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
        shortLabel: ({group}: AggregationLabelParams) => `Distinct property value count per ${group}`,
        perUserOperator: PRIMATIVE_MATH_TYPES.PER_USER_UNIQUE_VALUES,
        description: ({group}: AggregationLabelParams) =>
          `The number of distinct property values per ${group}\n\nAdditional functions available: Distribution, Average, Median, Minimum, Maximum${HELP_DOCS_LINK}`,
        subAggregations: [
          {
            label: `Average`,
            value: {
              selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
              operator: PRIMATIVE_MATH_TYPES.AVERAGE,
              perUserOperator: PRIMATIVE_MATH_TYPES.PER_USER_UNIQUE_VALUES,
              description: ({group}: AggregationLabelParams) =>
                `Average of the distinct property value counts per ${group}${HELP_DOCS_LINK}`,
            },
          },
          {
            label: `Distribution`,
            value: {
              selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
              operator: PRIMATIVE_MATH_TYPES.HISTOGRAM,
              perUserOperator: PRIMATIVE_MATH_TYPES.PER_USER_UNIQUE_VALUES,
              description: ({pluralGroup}: AggregationLabelParams) =>
                `A distribution of ${pluralGroup} by the distinct property value counts${HELP_DOCS_LINK}`,
            },
          },
          {
            label: `Median`,
            value: {
              selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
              operator: PRIMATIVE_MATH_TYPES.MEDIAN,
              perUserOperator: PRIMATIVE_MATH_TYPES.PER_USER_UNIQUE_VALUES,
              description: ({group}: AggregationLabelParams) =>
                `The total of the distinct property values counts per ${group}${HELP_DOCS_LINK}`,
            },
          },
          {
            label: `25th Percentile`,
            value: {
              selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
              operator: NUMERIC_MATH_TYPES.P25,
              perUserOperator: PRIMATIVE_MATH_TYPES.PER_USER_UNIQUE_VALUES,
              shortName: `P25`,
              description: ({group}: AggregationLabelParams) =>
                `The 25th percentile of the distinct property value counts per ${group}${HELP_DOCS_LINK}`,
            },
          },
          {
            label: `75th Percentile`,
            value: {
              selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
              operator: NUMERIC_MATH_TYPES.P75,
              perUserOperator: PRIMATIVE_MATH_TYPES.PER_USER_UNIQUE_VALUES,
              shortName: `P75`,
              description: ({group}: AggregationLabelParams) =>
                `The 75th percentile of the distinct property value counts per ${group}${HELP_DOCS_LINK}`,
            },
          },
          {
            label: `90th Percentile`,
            value: {
              selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
              operator: NUMERIC_MATH_TYPES.P90,
              perUserOperator: PRIMATIVE_MATH_TYPES.PER_USER_UNIQUE_VALUES,
              shortName: `P90`,
              description: ({group}: AggregationLabelParams) =>
                `The 90th percentile of the distinct property value counts per ${group}${HELP_DOCS_LINK}`,
            },
          },
          {
            label: `Minimum`,
            value: {
              selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
              operator: PRIMATIVE_MATH_TYPES.MIN,
              perUserOperator: PRIMATIVE_MATH_TYPES.PER_USER_UNIQUE_VALUES,
              description: ({group}: AggregationLabelParams) =>
                `Minimum of the distinct property value counts per ${group}${HELP_DOCS_LINK}`,
            },
          },
          {
            label: `Maximum`,
            value: {
              selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
              operator: PRIMATIVE_MATH_TYPES.MAX,
              perUserOperator: PRIMATIVE_MATH_TYPES.PER_USER_UNIQUE_VALUES,
              description: ({group}: AggregationLabelParams) =>
                `Maximum of the distinct property value counts per ${group}${HELP_DOCS_LINK}`,
            },
          },
        ],
      },
    },
    {
      label: `Minimum`,
      value: {
        selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
        operator: PRIMATIVE_MATH_TYPES.MIN,
        shortLabel: `Minimum of property value`,
        shortName: `Minimum`,
        description: `Minimum of a numeric property value across all instances of an event${HELP_DOCS_LINK}`,
      },
    },
    {
      label: `Minimum`,
      value: {
        selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
        shortLabel: ({group}: AggregationLabelParams) => `Minimum of property value per ${group}`,
        perUserOperator: PRIMATIVE_MATH_TYPES.PER_USER_MIN,
        description: ({group}: AggregationLabelParams) =>
          `Minimum of all the numeric property values per ${group}\n\nAdditional functions available: Distribution, Average, Median, Maximum${HELP_DOCS_LINK}`,
        subAggregations: [
          {
            label: `Average`,
            value: {
              selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
              operator: PRIMATIVE_MATH_TYPES.AVERAGE,
              perUserOperator: PRIMATIVE_MATH_TYPES.PER_USER_MIN,
              description: ({group}: AggregationLabelParams) =>
                `Average of the minimum of property values per ${group}${HELP_DOCS_LINK}`,
            },
          },
          {
            label: `Distribution`,
            value: {
              selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
              operator: PRIMATIVE_MATH_TYPES.HISTOGRAM,
              perUserOperator: PRIMATIVE_MATH_TYPES.PER_USER_MIN,
              description: ({pluralGroup}: AggregationLabelParams) =>
                `A distribution of ${pluralGroup} by the minimum of property values${HELP_DOCS_LINK}`,
            },
          },
          {
            label: `Median`,
            value: {
              selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
              operator: PRIMATIVE_MATH_TYPES.MEDIAN,
              perUserOperator: PRIMATIVE_MATH_TYPES.PER_USER_MIN,
              description: ({group}: AggregationLabelParams) =>
                `Median of the minimum of property values per ${group}${HELP_DOCS_LINK}`,
            },
          },
          {
            label: `25th Percentile`,
            value: {
              selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
              operator: NUMERIC_MATH_TYPES.P25,
              perUserOperator: PRIMATIVE_MATH_TYPES.PER_USER_MIN,
              shortName: `P25`,
              description: ({group}: AggregationLabelParams) =>
                `The 25th percentile of the minimum of property values per ${group}${HELP_DOCS_LINK}`,
            },
          },
          {
            label: `75th Percentile`,
            value: {
              selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
              operator: NUMERIC_MATH_TYPES.P75,
              perUserOperator: PRIMATIVE_MATH_TYPES.PER_USER_MIN,
              shortName: `P75`,
              description: ({group}: AggregationLabelParams) =>
                `The 75th percentile of the minimum of property values per ${group}${HELP_DOCS_LINK}`,
            },
          },
          {
            label: `90th Percentile`,
            value: {
              selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
              operator: NUMERIC_MATH_TYPES.P90,
              perUserOperator: PRIMATIVE_MATH_TYPES.PER_USER_MIN,
              shortName: `P90`,
              description: ({group}: AggregationLabelParams) =>
                `The 90th percentile of the minimum of property values per ${group}${HELP_DOCS_LINK}`,
            },
          },
          {
            label: `Maximum`,
            value: {
              selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
              operator: PRIMATIVE_MATH_TYPES.MAX,
              perUserOperator: PRIMATIVE_MATH_TYPES.PER_USER_MIN,
              description: ({group}: AggregationLabelParams) =>
                `Maximum of the minimum of property values per ${group}${HELP_DOCS_LINK}`,
            },
          },
        ],
      },
    },
    {
      label: `Maximum`,
      value: {
        selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
        operator: PRIMATIVE_MATH_TYPES.MAX,
        shortLabel: `Maximum of property value`,
        shortName: `Maximum`,
        description: `Maximum of a numeric property value across all instances of an event${HELP_DOCS_LINK}`,
      },
    },
    {
      label: `Maximum`,
      value: {
        selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
        shortLabel: ({group}: AggregationLabelParams) => `Maximum of property value per ${group}`,
        perUserOperator: PRIMATIVE_MATH_TYPES.PER_USER_MAX,
        description: ({group}: AggregationLabelParams) =>
          `Maximum of all the numeric property values per ${group}\n\nAdditional functions available: Distribution, Average, Median, Minimum${HELP_DOCS_LINK}`,
        subAggregations: [
          {
            label: `Average`,
            value: {
              selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
              operator: PRIMATIVE_MATH_TYPES.AVERAGE,
              perUserOperator: PRIMATIVE_MATH_TYPES.PER_USER_MAX,
              description: ({group}: AggregationLabelParams) =>
                `Average of the maximum of property values per ${group}`,
            },
          },
          {
            label: `Distribution`,
            value: {
              selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
              operator: PRIMATIVE_MATH_TYPES.HISTOGRAM,
              perUserOperator: PRIMATIVE_MATH_TYPES.PER_USER_MAX,
              description: ({pluralGroup}: AggregationLabelParams) =>
                `A distribution of ${pluralGroup} by the maximum of property values${HELP_DOCS_LINK}`,
            },
          },
          {
            label: `Median`,
            value: {
              selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
              operator: PRIMATIVE_MATH_TYPES.MEDIAN,
              perUserOperator: PRIMATIVE_MATH_TYPES.PER_USER_MAX,
              description: ({group}: AggregationLabelParams) =>
                `Median of the maximum of property values per ${group}${HELP_DOCS_LINK}`,
            },
          },
          {
            label: `25th Percentile`,
            value: {
              selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
              operator: NUMERIC_MATH_TYPES.P25,
              perUserOperator: PRIMATIVE_MATH_TYPES.PER_USER_MAX,
              shortName: `P25`,
              description: ({group}: AggregationLabelParams) =>
                `The 25th percentile of the maximum of property values per ${group}${HELP_DOCS_LINK}`,
            },
          },
          {
            label: `75th Percentile`,
            value: {
              selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
              operator: NUMERIC_MATH_TYPES.P75,
              perUserOperator: PRIMATIVE_MATH_TYPES.PER_USER_MAX,
              shortName: `P75`,
              description: ({group}: AggregationLabelParams) =>
                `The 75th percentile of the maximum of property values per ${group}${HELP_DOCS_LINK}`,
            },
          },
          {
            label: `90th Percentile`,
            value: {
              selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
              operator: NUMERIC_MATH_TYPES.P90,
              perUserOperator: PRIMATIVE_MATH_TYPES.PER_USER_MAX,
              shortName: `P90`,
              description: ({group}: AggregationLabelParams) =>
                `The 90th percentile of the maximum of property values per ${group}${HELP_DOCS_LINK}`,
            },
          },
          {
            label: `Minimum`,
            value: {
              selectorType: SelectorTypeDefinitions.EventPropertyAggregation,
              operator: PRIMATIVE_MATH_TYPES.MIN,
              perUserOperator: PRIMATIVE_MATH_TYPES.PER_USER_MAX,
              description: ({group}: AggregationLabelParams) =>
                `Minimum of the maximum of property values per ${group}${HELP_DOCS_LINK}`,
            },
          },
        ],
      },
    },
  ],

  // count user profile property aggregations
  [SelectorTypeDefinitions.UserPropertyAggregation]: [
    {
      label: `Sum`,
      value: {
        selectorType: SelectorTypeDefinitions.UserPropertyAggregation,
        operator: PRIMATIVE_MATH_TYPES.TOTAL,
        shortLabel: `Sum of property value`,
        description: `Counts the total of a numeric property value across all profiles${HELP_DOCS_LINK}`,
      },
    },
    {
      label: `Average`,
      value: {
        selectorType: SelectorTypeDefinitions.UserPropertyAggregation,
        operator: PRIMATIVE_MATH_TYPES.AVERAGE,
        shortLabel: `Average of property value`,
        description: `Adds the numeric property values across all profiles and divides by the total count${HELP_DOCS_LINK}`,
      },
    },
    {
      label: `Median`,
      value: {
        selectorType: SelectorTypeDefinitions.UserPropertyAggregation,
        operator: PRIMATIVE_MATH_TYPES.MEDIAN,
        shortLabel: `Median of property value`,
        description: `Counts the numeric property value across all profiles in order and finds the middle value${HELP_DOCS_LINK}`,
      },
    },
    {
      label: `Distinct Count`,
      value: {
        selectorType: SelectorTypeDefinitions.UserPropertyAggregation,
        operator: PRIMATIVE_MATH_TYPES.UNIQUE_VALUES,
        shortLabel: `Distinct property value count`,
        description: `The number of distinct property values across all profiles${HELP_DOCS_LINK}`,
      },
    },
    {
      label: `25th Percentile`,
      value: {
        selectorType: SelectorTypeDefinitions.UserPropertyAggregation,
        operator: NUMERIC_MATH_TYPES.P25,
        shortLabel: `P25 of property value`,
        shortName: `P25`,
        description: `Ranks the 25th percentile of the numeric property value across all profiles${HELP_DOCS_LINK}`,
      },
    },
    {
      label: `75th Percentile`,
      value: {
        selectorType: SelectorTypeDefinitions.UserPropertyAggregation,
        operator: NUMERIC_MATH_TYPES.P75,
        shortLabel: `P75 of property value`,
        shortName: `P75`,
        description: `Ranks the 75th percentile of the numeric property value across all profiles${HELP_DOCS_LINK}`,
      },
    },
    {
      label: `90th Percentile`,
      value: {
        selectorType: SelectorTypeDefinitions.UserPropertyAggregation,
        operator: NUMERIC_MATH_TYPES.P90,
        shortLabel: `P90 of property value`,
        shortName: `P90`,
        description: `Ranks the 90th percentile of the numeric property value across all profiles${HELP_DOCS_LINK}`,
      },
    },
    {
      label: `99th Percentile`,
      value: {
        selectorType: SelectorTypeDefinitions.UserPropertyAggregation,
        operator: NUMERIC_MATH_TYPES.P99,
        shortLabel: `P99 of property value`,
        shortName: `P99`,
        description: `Ranks the 99th percentile of the numeric property value across all profiles${HELP_DOCS_LINK}`,
      },
    },
    {
      label: `Minimum`,
      value: {
        selectorType: SelectorTypeDefinitions.UserPropertyAggregation,
        operator: PRIMATIVE_MATH_TYPES.MIN,
        shortLabel: `Min of property value`,
        shortName: `Min`,
        description: `The smallest value of a numeric property across all profiles${HELP_DOCS_LINK}`,
      },
    },
    {
      label: `Maximum`,
      value: {
        selectorType: SelectorTypeDefinitions.UserPropertyAggregation,
        operator: PRIMATIVE_MATH_TYPES.MAX,
        shortLabel: `Max of property value`,
        shortName: `Max`,
        description: `The greatest value of a numeric property across all profiles${HELP_DOCS_LINK}`,
      },
    },
  ],
};

export const FlattenSelectorTypeAggregations: SelectorTypeAggregationItem[] = sortBy(
  Object.keys(SelectorTypeAggregations).reduce((items, type: SelectorType) => {
    return [
      ...items,
      ...flatMap(
        SelectorTypeAggregations[type].map((item) =>
          item.value.subAggregations ? [item, ...item.value.subAggregations] : item,
        ),
      ),
    ];
  }, []),
  (item) => item.value.order || 0,
);

const mapItemToSubsections = (item: SelectorTypeAggregationItem) => ({
  ...item,
  subLabel: ``,
  subSections: item.value.subAggregations ? [{items: item.value.subAggregations}] : null,
});

export function nestedSelectorTypeAggregationsSections(types: SelectorType[]): SelectorTypeAggregationSection[] {
  return [
    {
      items: [
        // show all event operators except session count
        SelectorTypeAggregations[ResourceType.Event][0],
        // count uniques
        {
          ...SelectorTypeAggregations[ResourceType.User][0],
        },
        // count profiles for people mode
        ...(types.includes(ResourceType.User)
          ? [SelectorTypeAggregations[ResourceType.User].find((item) => item.value.selectorType === ResourceType.User)]
          : []),
        // total per user
        SelectorTypeAggregations[ResourceType.Event][1],
        // aggregate property values
        {
          label: `Aggregate Properties`,
          subSections: [
            {
              items: [
                ...SelectorTypeAggregations[SelectorTypeDefinitions.EventPropertyAggregation].filter(
                  (item) => !item.value.perUserOperator,
                ),
                // people mode aggregations
                ...SelectorTypeAggregations[SelectorTypeDefinitions.UserPropertyAggregation],
              ],
            },
          ],
        },
        // count rolling uniques for metrics mode
        ...(!types.includes(ResourceType.User)
          ? [
              {
                label: ({pluralGroupTitle}: AggregationLabelParams) => `Count ${pluralGroupTitle}`,
                subSections: [
                  {
                    items: SelectorTypeAggregations[ResourceType.User]
                      .filter((item) => item.value.operator !== PRIMATIVE_MATH_TYPES.UNIQUE)
                      .map(mapItemToSubsections),
                  },
                ],
              },
            ]
          : []),
        // aggregate properties per user
        ...(types.includes(SelectorTypeDefinitions.EventPropertyAggregation)
          ? [
              {
                label: ({group}: AggregationLabelParams) => `Aggregate Properties per ${capitalize(group)}`,
                subSections: [
                  {
                    items: SelectorTypeAggregations[SelectorTypeDefinitions.EventPropertyAggregation].filter(
                      (item) => !!item.value.perUserOperator,
                    ),
                  },
                ],
              },
            ]
          : []),
        // count sessions containing a given event
        {
          ...SelectorTypeAggregations[ResourceType.Event].find(
            (item) => item.value.operator === PRIMATIVE_MATH_TYPES.SESSIONS,
          ),
          label: `Count Sessions`,
        },
      ],
    },
  ];
}

export const SelectorTypeAggregationsSections: SelectorTypeAggregationSection[] = [
  {
    label: `Events`,
    // show all event operators except session count
    items: SelectorTypeAggregations[ResourceType.Event].filter(
      (item) => item.value.operator !== PRIMATIVE_MATH_TYPES.SESSIONS,
    ),
  },
  {
    label: ({pluralGroupTitle}: AggregationLabelParams) => `${pluralGroupTitle}`,
    items: SelectorTypeAggregations[ResourceType.User],
  },
  {
    label: `Event Property Values`,
    items: SelectorTypeAggregations[SelectorTypeDefinitions.EventPropertyAggregation].filter(
      (item) => !item.value.perUserOperator,
    ),
  },
  {
    label: `Event Property Values Per User`,
    items: SelectorTypeAggregations[SelectorTypeDefinitions.EventPropertyAggregation].filter(
      (item) => !!item.value.perUserOperator,
    ),
  },
  {
    label: `Profile Property Value`,
    items: SelectorTypeAggregations[SelectorTypeDefinitions.UserPropertyAggregation],
  },
  {
    label: `Sessions`,
    items: SelectorTypeAggregations[ResourceType.Event].filter(
      (item) => item.value.operator === PRIMATIVE_MATH_TYPES.SESSIONS,
    ),
  },
];

export const DefaultCustomBucket: Bucket = {
  bucketSize: 10,
  min: 0,
  max: 100,
  offset: null,
};
